import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { color, ColorResult, validHex } from '@uiw/react-color';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../connection/socket';
import { CreateMediaFromGenerateContentSound, UpsertMediaDraft } from '../../connection/media';
import useShots from '../../contexts/shots/provider';
import { saveMediaItem, updateSingleMedia } from '../../stores/actions/media';
import { setModalState } from '../../stores/actions/modal';
import { FEED_TYPES } from '../../stores/reducers/shots';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import type { MediaItem } from '../../stores/types/media';
import type { ShotFeed } from '../../stores/types/shots';
import type { GreenroomUser } from '../../stores/types/session';
import { GreenroomState } from '../../stores/types/types';
import { isVideo } from '../../utils/utils';
import { ICONS } from '../general-components/icon';
import RightClickMenu, { MenuItem } from '../general-components/right-click-menu';
import { VIDEO_EDITOR_PAGES } from '../modal/templates/video-editor/video-editor';
import useMedia from '../../hooks/use-media';
import ColorPicker from '../right-panel/settings/color-pickers/color-picker';

const filterHidden = (i: MenuItem) => !i.hidden;
interface Props {
	clickCoordinates?: [number, number];
	contextMenuId: string;
	mediaItem: MediaItem;
	socket: Socket | null;
	user: GreenroomUser;
	canEditMedia: boolean;
	isForShotEditorModal?: boolean;
}

const baseColorHex = '#151515'
const baseColorResult = (hex?: any): ColorResult | undefined => {
	if (hex === 'none') return undefined;
	if (validHex(hex)) return color(hex);
	return color(baseColorHex)
}

export default function FeedMediaContextMenu(props: Props) {
	const dispatch = useDispatch();
	const {
		clickCoordinates,
		contextMenuId,
		mediaItem,
		socket,
		user,
		isForShotEditorModal = false,
	} = props;

	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const contentTemplates = useSelector((state: GreenroomState) => state.GeneratedContentReducer.contentTemplates);
	const { pushShotFeedToCurrentShot } = useShots();

	const mediaSettingsDebounce = useRef<NodeJS.Timeout | null>(null);
	const timmy = useRef<NodeJS.Timeout | null>(null); // a little lamb with a lot to learn

	const [lastColorProps, setLastColorProps] = useState<ColorResult | undefined>();
	const [colorProps, setColorProps] = useState<ColorResult | undefined>(baseColorResult(mediaItem.media_data.background_color));

	const {
		canTrimMediaItem,
	} = useMedia();

	const canTrim = useMemo((): boolean => (
		canTrimMediaItem(mediaItem)
	), [canTrimMediaItem, mediaItem]);

	const handleMediaSettings = (type: string, value: boolean | string) => {
		if (!mediaItem || !token || !user) return;
		if (mediaSettingsDebounce.current) clearTimeout(mediaSettingsDebounce.current);

		mediaSettingsDebounce.current = setTimeout(() => {
			const newMediaData = JSON.parse(JSON.stringify(mediaItem.media_data));
			const updatedMediaItem = { ...mediaItem, media_data: newMediaData, edited_by: user.gr_user };
			if (newMediaData[type] !== value) {
				newMediaData[type] = value;
				if (isForShotEditorModal) {
					UpsertMediaDraft(updatedMediaItem.meeting, updatedMediaItem);
				} else {
					dispatch(
						saveMediaItem(
							token,
							updatedMediaItem,
							undefined,
						),
					);
					socket?.setMedia(updatedMediaItem);
				}
			}
		}, 100);
	};

	// Debounces background color updates
	useEffect(() => {
		if (timmy.current) clearTimeout(timmy.current);
		timmy.current = setTimeout(() => {
			if (colorProps?.hex) {
				handleMediaSettings('background_color', colorProps.hex);
			} else {
				handleMediaSettings('background_color', 'none');
			}
		}, 300);
	}, [colorProps]);

	const toggleBackgroundActive = () => {
		if (mediaItem.media_data.background_color && mediaItem.media_data.background_color !== 'none') {
			setLastColorProps(colorProps);
			setColorProps(undefined);
			handleMediaSettings('background_color', 'none');
		} else {
			const newColor = lastColorProps ?? baseColorResult(baseColorHex);
			setColorProps(newColor);
			handleMediaSettings('background_color', newColor?.hex ?? baseColorHex);
		}
	};

	const handleTrim = () => {
		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.VIDEO_EDITOR,
			modalData: {
				mediaId: mediaItem.id,
				editorPage: mediaItem.num_pages > 1
					? VIDEO_EDITOR_PAGES.SELECT_MEDIA_PAGE
					: VIDEO_EDITOR_PAGES.TRIM_EDITOR,
			},
		}));
	};

	const menuItems: MenuItem[] = useMemo(() => [
		{
			hidden: !mediaItem.media_data.fill,
			handleClick: () => handleMediaSettings('fill', false),
			iconName: ICONS.FIT,
			text: 'Fit',
			dontClose: true,
		},
		{
			hidden: mediaItem.media_data.fill,
			handleClick: () => handleMediaSettings('fill', true),
			iconName: ICONS.FULLSCREEN,
			text: 'Fill',
			dontClose: true,
		},
		{
			hidden: !isVideo(mediaItem.images),
			handleClick: async () => {
				const newLoop = !mediaItem.media_data.loop;
				handleMediaSettings('loop', newLoop);

				if (mediaItem.template_type && token && meeting?.meeting_id) {
					const template = contentTemplates.find(t => t.type === mediaItem.template_type);
					if (!template?.stylePack) return;

					const contentType = newLoop ? 'audio_version_loopable' : 'audio_version';
					const targetDuration = Math.floor(mediaItem.media_data.duration_map?.[0] || template.data?.duration);
					let audioItem = targetDuration
						? contentTemplates.find(
							x => x.stylePack === template.stylePack
								&& x.contentType?.toString() === contentType
								&& x.data?.config?.duration == targetDuration
						)
						: undefined;
					if (!audioItem) {
						audioItem = contentTemplates.find(
							x => x.stylePack === template.stylePack && x.contentType?.toString() === contentType
						);
					}
					if (!audioItem?.videoUrl) return;

					const newAudioMediaItem = (await CreateMediaFromGenerateContentSound(
						token,
						meeting.meeting_id,
						audioItem.type,
						audioItem.data?.id,
					)) as MediaItem;

					dispatch(updateSingleMedia(newAudioMediaItem));

					const newFeed: ShotFeed = {
						position: 0,
						feedType: FEED_TYPES.MEDIA_AUDIO,
						media: newAudioMediaItem.id,
						audioOnly: true,
					} as ShotFeed;

					pushShotFeedToCurrentShot(newFeed, true);
				}
			},
			checkboxValue: !!mediaItem.media_data.loop,
			text: 'Loop',
			dontClose: true,
		},
		{
			handleClick: toggleBackgroundActive,
			checkboxValue: mediaItem.media_data.background_color !== 'none',
			text: 'Background',
			dontClose: true,
		},
		{
			iconName: ICONS.DESIGN,
			text: 'Color',
			disabled: mediaItem.media_data.background_color === 'none',
			CustomSubMenu: (
				<ColorPicker
					open={true}
					openHexAtStart={colorProps?.hex ?? mediaItem?.media_data?.background_color}
					onChange={(color: ColorResult) => setColorProps(color)}
					customPosition={{top:-240,left:1}}
				/>
			),
			dontClose: true,
		},
		{
			hidden: !canTrim,
			handleClick: handleTrim,
			iconName: ICONS.MEDIA_TRIM,
			text: 'Trim',
		},

	], [colorProps, mediaItem, token, meeting, contentTemplates, pushShotFeedToCurrentShot]);

	const nClickCoordinates = useMemo((): [number, number] | undefined => {
		if (!clickCoordinates) return clickCoordinates;
		const shownItems = menuItems.filter(filterHidden);
		const newY = 36 * (shownItems.length);
		return [clickCoordinates[0] - 4, clickCoordinates[1] + newY]
	}, [clickCoordinates, menuItems.length])

	return (
	<RightClickMenu
		contextMenuId={contextMenuId}
		items={menuItems}
		clickCoordinates={nClickCoordinates}
	/>);
}
